import React, { useEffect, useState } from "react";
import {
  BookOpen,
  Check,
  ChevronLeft,
  Copy,
  Gift,
  HelpCircle,
  Megaphone,
  Send,
  Share2,
  ShoppingCart,
  Sparkles,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import instance from "../../../axios";
import Logo from "../../../Images/Adhyayi Logo.png";
import { toast } from "react-toastify";
import { RiArrowRightCircleFill } from "react-icons/ri";

const InviteFriend = () => {
  const [referralCode, setReferralCode] = useState("");
  const [referralStats, setReferralStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [friendCode, setFriendCode] = useState("");
  const [referralConfig, setReferralConfig] = useState(null);

  const fetchReferralData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        instance.get("/referral/stats"),
        instance.get("/referral/history?page=1&limit=5"),
      ]);

      if (statsRes.data?.result) {
        setReferralCode(statsRes.data.result.referralCode || "");
        setReferralStats(statsRes.data.result);
      }

      if (historyRes.data?.result?.referrals) {
        setHistory(historyRes.data.result.referrals);
      }
    } catch (error) {
      console.error("Referral data error", error);
    }
  };

  useEffect(() => {
    fetchReferralData();
  }, []);
  const getReferralConfig = async () => {
  try {
    const res = await instance.get("/admin/referral/config");

    if (res.data.success) {
      setReferralConfig(res.data.result);
    }
  } catch (err) {
    console.log(err);
  }
};

useEffect(() => {
  getReferralConfig();
}, []);

  const shareableLink = referralCode
    ? `https://adhyayi.com/login?ref=${referralCode}`
    : "";

  const shareText = `Join Adhyayi and start earning while learning! Use my referral code: ${referralCode}\n${shareableLink}`;

  const copyToClipboard = () => {
    if (!shareableLink && !referralCode) return;

    navigator.clipboard.writeText(shareableLink || referralCode);
    setCopied(true);

    toast.success("Invite link copied");

    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!referralCode) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Refer & Earn",
          text: shareText,
          url: shareableLink,
        });
        return;
      } catch {
        // Fall back to WhatsApp if native share is cancelled or unavailable.
      }
    }

    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`,
      "_blank",
    );
  };

  const formatTime = (date) => {
    if (!date) return "Recently";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getReward = (ref) => ref?.referrerReward?.amount || ref?.reward || 0;

  const activityText = (ref) => {
    const name = ref?.referred?.name || ref?.referred?.mobile || "Friend";
    if (ref?.status === "plan_purchased") {
      return `${name} purchased a course`;
    }
    if (ref?.status === "rewarded") {
      return `${name} completed referral task`;
    }
    return `${name} joined using your code`;
  };

  const statCards = [
    {
      label: "Total Referrals",
      value: referralStats?.totalReferrals || 0,
      helper: "Friends joined",
      icon: Users,
      color: "text-[#6E39E8]",
      bg: "bg-[#F1ECFF]",
    },
    {
      label: "Total Earned",
      value: `${referralStats?.totalEarned || referralStats?.totalRewards || 0}`,
      helper: "All time rewards",
      icon: Trophy,
      color: "text-[#F5A623]",
      bg: "bg-[#FFF3D8]",
    },
    {
      label: "Total Credits",
      value:
        referralStats?.currentBalance ||
        referralStats?.referralBalance ||
        referralStats?.totalEarned ||
        0,
      helper: "Ready to use",
      icon: Gift,
      color: "text-[#FF7A1A]",
      bg: "bg-[#FFF0DF]",
    },
  ];

  const steps = [
  {
    title: "Share with Friend",
    text: "Share your referral code or invite link with your friends.",
    icon: Share2,
    bg: "bg-[#6E39E8]",
  },
  {
    title: "If Friend Installs",
    text: (
      <>
        When your friend installs the app, you instantly receive{" "}
        <span className="font-bold text-orange-500">{referralConfig?.signupReward?.amount} Credits</span>.
      </>
    ),
    icon: Sparkles,
    bg: "bg-[#B23CFF]",
  },
  {
    title: "If Friend Buys a Course",
    text: (
      <>
        When your friend makes their first purchase, you get{" "}
        <span className="font-bold text-orange-500">{referralConfig?.planPurchaseReward?.amount} Credits</span>.
      </>
    ),
    icon: ShoppingCart,
    bg: "bg-[#F59E0B]",
  },
  {
    title: "First Recharge Bonus",
    text: (
      <>
        When your friend completes their first wallet recharge, earn{" "}
        <span className="font-bold text-orange-500">{referralConfig?.firstRechargeReward?.amount} Credits</span>.
      </>
    ),
    icon: Zap,
    bg: "bg-[#37216E]",
  },
];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F4ECFA] px-4 pb-24 pt-4 text-[#25123E] lg:px-8 lg:pb-10">
      {/* Background Circles */}
      {/* Background Circles */}
      <div className="absolute -top-16 -left-12 w-[300px] h-[300px] bg-[#8E7C9F] rounded-full opacity-30 z-0"></div>

      <div className="absolute -bottom-16 -right-20 w-[260px] h-[260px] bg-orange-300 rounded-full opacity-30 z-0"></div>

      <div className="mx-auto max-w-5xl relative z-10 lg:pt-10">
        <div className="mb-2 flex items-center justify-between">
          {/* <button
            type="button"
            onClick={() => window.history.back()}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#6E39E8] shadow-sm"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button> */}

          <button
            type="button"
            onClick={handleShare}
            className="flex h-9 items-center gap-2 rounded-full bg-white px-4 text-xs font-bold text-[#6E39E8] shadow-sm"
          >
            <Share2 className="h-4 w-4" />
            Share
          </button>
        </div>

        <section className="relative mb-0 overflow-hidden rounded-[1.7rem] p-1">
          <div className="relative min-h-[150px]">
            <div className="max-w-[62%] pt-0 text-left">
              <h1 className="text-3xl font-semibold ">
                <span className="text-purple-800"> Refer & Earn</span>
              </h1>
              <p className="mt-3 text-md leading-5 text-black">
                Invite your friends, help them learn, and earn <span className="text-purple-800 font-semibold">exciting rewards
                  together!</span>
              </p>
            </div>

            <div className="absolute right-2 top-0 h-20 w-20 rotate-[8deg] rounded-3xl bg-[#8D39E8] shadow-[0_18px_30px_rgba(92,39,160,0.28)]">
              <div className="flex h-full items-center justify-center text-white">
                <Gift className="h-10 w-10" />
              </div>
            </div>
            <div className="absolute right-0 top-16 flex h-14 w-14 items-center justify-center rounded-full bg-[#1E8BFF] text-white shadow-lg">
              <Megaphone className="h-7 w-7" />
            </div>
          </div>
        </section>

        <div className="lg:flex lg:flex-cols- lg:items-start lg:gap-5 text-left">

          {/* REFERRAL CARD */}
          <section className="relative overflow-hidden mb-5 rounded-[2rem] bg-gradient-to-br from-[#7B2FF7] via-[#5B1CCB] to-[#42107A] p-5 text-white shadow-[0_18px_35px_rgba(45,19,86,0.25)]">

            {/* BG CIRCLES */}
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full"></div>
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full"></div>

            {/* TOP */}
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                  Your Referral Code
                </p>
              </div>

              <button
                onClick={handleShare}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              >
                <Share2 className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* CODE BOX */}
            <div className="mt-3 relative z-10">
              <div className="border-2 border-dashed border-white/30 rounded-[30px] px-5 py-2 flex items-center justify-between bg-white/10 backdrop-blur-sm">

                <p className="text-2xl font-black tracking-wide">
                  {referralCode || "LOADING"}
                </p>

                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center"
                >
                  <Copy className="h-3 w-3 text-white" />
                </button>
              </div>

              <p className="mt-2 text-sm text-white/80 leading-6 max-w-[90%]">
                Share your code and invite friends. They get started, you earn rewards.
              </p>
            </div>

            {/* SHARE BUTTON */}
            <button
              type="button"
              onClick={handleShare}
              className="relative z-10 mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#FFCA80] to-[#FF9800] text-lg font-bold text-white shadow-[0_10px_25px_rgba(255,152,0,0.35)]"
            >
              <Share2 className="h-5 w-5 item-fill" />
              Share Now
            </button>
          </section>

          <section className="mb-5 grid grid-cols-3 gap-2  lg:order-2 lg:flex lg:flex-cols lg:gap-5">
            {statCards.map((card, index) => {
              const Icon = card.icon;

              return (
                <div
                  key={card.label}
                  className={`rounded-3xl bg-white px-5 py-2 text-left shadow-[0_10px_24px_rgba(62,34,91,0.08)] flex flex-col justify-between min-h-[100px] lg:min-h-[180px] lg:px-6 lg:py-6 ${index === 2 ? "border-2 border-green-300" : ""
                    }`}
                >
                  <div>
                    <div
                      className={`mb-2 flex h-12 w-12 items-center justify-center rounded-full ${card.bg} ${card.color}`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>

                    <p className="text-2xl font-extrabold text-[#25123E] lg:text-3xl">
                      {card.value}
                    </p>

                    <p className="mt-2 text-sm font-bold leading-5 text-[#453852] lg:text-base">
                      {card.label}
                    </p>

                    <p className="mt-1 text-xs text-gray-400 lg:text-sm">
                      {card.helper}
                    </p>
                  </div>

                  {/* Last Bottom Active Dot */}
                  {index === 2 && (
                    <div className="mt-5 flex justify-center">
                      <span className="h-3 w-3 rounded-full bg-green-500 animate-pulse"></span>
                    </div>
                  )}
                </div>
              );
            })}
          </section>


        </div>
        <section className="mb-5 rounded-[1.5rem] bg-white p-5 shadow-[0_10px_24px_rgba(62,34,91,0.08)] lg:order-4 lg:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#2D1356]">
              <Sparkles className="h-4 w-4 text-[#FF7A1A]" />
              How Adhyayi Referral Works
            </h2>
            <HelpCircle className="h-5 w-5 text-gray-300" />
          </div>

          <div className="space-y-5">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="relative flex gap-4">
                  {index < steps.length - 1 && (
                    <div className="absolute left-5 top-10 h-[calc(100%+0.25rem)] w-px bg-[#E6D8F3]" />
                  )}

                  <div
                    className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white ${step.bg}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  <div>
                    <p className=" flex flex-row gap-2 text-sm font-bold text-[#25123E]">
                      <span className="bg-purple-800 text-white pl-1 pb-1 w-4 h-4 rounded-full"> {index + 1} </span> {step.title}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-[#756985]">
                      {step.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        <section className="rounded-[1.5rem] z-[60] bg-[#F7F0FB] p-5 shadow-[0_10px_24px_rgba(62,34,91,0.08)]">
          <div className="mb-4 items-left text-left justify-between">
            <h2 className="font-bold text-[#2D1356]">
              Recent Activity
            </h2>
            <p>Your newest referral updates will appear here</p>

          </div>

          {history.length === 0 ? (
            <div className="rounded-2xl bg-[#F7F0FB] px-4 py-6 text-center">
              <Gift className="mx-auto mb-2 h-10 w-10 p-1 text-[#9A80B8] bg-purple-300 rounded-full" />
              <p className="text-sm font-bold text-purple-600">
                No referrals yet
              </p>
              <p className="mt-1 text-xs text-[#756985]">
                Share your link to start seeing signups,installs,and rewards here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((ref, index) => {
                const reward = getReward(ref);
                return (
                  <div key={ref._id || index} className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F7F0FB]">
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                          ref?.referred?.name || "User",
                        )}&background=6E39E8&color=fff&size=96`}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-[#25123E]">
                        {activityText(ref)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatTime(ref.createdOn)}
                      </p>
                    </div>
                    <p className="shrink-0 text-xs font-extrabold text-green-600">
                      {reward > 0 ? `+${reward} Credits` : "Pending"}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[#2D1356] p-3 text-white z-[60]">
            {/* <img
              src={Logo}
              alt="Adhyayi"
              className="h-10 w-10 rounded-full bg-white object-contain p-1"
            /> */}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">Invite to Earn More!</p>
              <p className="text-[11px] text-white/65">Grow together</p>
            </div>
            <button
              type="button"
              onClick={handleShare}
              className="rounded-full bg-[#FFB11B] px-4 py-2 text-xs font-extrabold text-white"
            >
              Invite Now
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InviteFriend;
